import React, {useState} from 'react';
import {Appbar, Menu} from 'react-native-paper';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  setShowComments,
  setShowConfig,
} from '../../store/slices/editor';
import {
  createNewParty,
  getActiveProjectLayer,
  getById,
  writeItem,
} from '../../api';
import {useQuery, useQueryClient} from 'react-query';
import {
  EditorComment,
  EditorLayer,
  EncoderProject,
} from '../../types';
import {getFolderContent, getUserData} from '../../api';

import uuid from 'react-native-uuid';
import {Platform, useWindowDimensions} from 'react-native';

export default function EditorToolBar(props) {
  const {projectId, routeParams} = props;
  const {encoder} = routeParams;
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const caretPosition = useAppSelector(
    state => state.editor.caretPosition,
  );
  const scrollPosition = useAppSelector(
    state => state.editor.scrollPosition,
  );
  const pixelsPerSecond = useAppSelector(
    state => state.editor.pixelsPerSecond,
  );
  const showComments = useAppSelector(
    state => state.editor.showComments,
  );
  const showConfig = useAppSelector(state => state.editor.showConfig);
  const {width} = useWindowDimensions();
  const isMobile = width < 700 || Platform.OS !== 'web';
  const showEditor = useAppSelector(state => state.editor.showEditor);
  const currentPosition =
    (caretPosition + scrollPosition) / pixelsPerSecond;
  const [isRefreshing, setRefreshing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const onHideMenu = () => setMenuOpen(false);
  const openMenu = () => setMenuOpen(true);

  const layersQuery = useQuery(
    ['projectLayersToolbar', projectId],
    async () => {
      const data = await getFolderContent(projectId);
      const m = await Promise.all(
        data.map(async i => {
          return {
            ...i,
            media: await getFolderContent(i?.id),
          };
        }),
      );
      return m;
    },
  );
  const layers: EditorLayer[] | undefined = layersQuery.data;
  const forgetEverything = async () => {
    await layersQuery.refetch();
    await queryClient.refetchQueries(['folderData_' + projectId], {});
    await queryClient.refetchQueries(['folderContent_' + projectId]);
    await queryClient.refetchQueries(['project_' + projectId]);
    await queryClient.refetchQueries(['projectLayers', projectId]);
    await queryClient.refetchQueries(['encodedMedia', projectId]);
    await queryClient.refetchQueries(projectId + '_comments');
    const folders = layers?.map(layer => layer.id);
    console.info('project.folders', folders);
    if (folders?.length)
      for (let f = 0; f < folders.length; f++) {
        const fId = folders[f];
        queryClient.refetchQueries(['folderContent_' + fId]);
      }
  };

  const onClickCopy = async () => {
    if (isRefreshing) {
      return;
    }
    setRefreshing(true);
    try {
      const item = await getById(encoder, projectId);
      item.isCopy = item.isCopy || item.id;
      item.id = uuid.v4().toString();
      item.orderId += 1;
      item.createdAt = new Date().valueOf().toString();
      console.info('click copy', {encoder, currentPosition, item});
      await writeItem(item);
      await queryClient.refetchQueries(['projectLayers', projectId]);
      await queryClient.refetchQueries([
        'folderContent_' + item.folderId,
      ]);
      await forgetEverything();
    } catch (err) {
      console.error('oncClickCopy', err);
    }
    setRefreshing(false);
  };

  const onClickCut = async () => {
    if (isRefreshing) {
      return;
    }
    setRefreshing(true);
    try {
      const currentData = getActiveProjectLayer(
        layers,
        currentPosition,
      );
      const item: EncoderProject = await getById(encoder, projectId);
      const offsetPosition =
        currentPosition -
        currentData.offset +
        (item.encoding.start || 0);

      const nitem = JSON.parse(JSON.stringify({...item}));
      nitem.isCopy = item.isCopy || item.id;
      nitem.id = uuid.v4().toString();
      nitem.orderId -= 1;
      nitem.encoding.start = offsetPosition;
      nitem.encoding.end = item.encoding.end || item.duration || 0;
      nitem.createdAt = new Date().valueOf().toString();

      item.encoding.end = offsetPosition;
      await writeItem(item);
      await writeItem(nitem);
      await forgetEverything();
    } catch (err) {
      console.error('oncClickCut', err);
    }
    setRefreshing(false);
  };

  const onClickRefresh = async () => {
    try {
      if (isRefreshing) {
        return;
      }
      setRefreshing(true);

      await forgetEverything();
    } catch (err) {
      console.error('onClickRefresh', err);
    }
    setRefreshing(false);
  };

  const onClickShowComments = () =>
    dispatch(setShowComments(!showComments));

  const onClickAddComment = async () => {
    const user = await getUserData();
    const id = uuid.v4().toString();
    const mnemonic = routeParams.mnemonic;
    console.info('create contact with mnemonic', mnemonic);
    const comment: EditorComment = {
      id,
      partyId: id,
      userId: user.id,
      title: user.name,
      currentPosition,
      isContact: true,
      name: user.name,
      mnemonic,
      isEditorComment: true,
      folderId: props.projectId + '_comments',
    };
    await createNewParty(comment);
    await queryClient.refetchQueries(props.projectId + '_comments');
  };

  return (
    <>
      {!isMobile ? (
        <>
          <Appbar.Action
            icon="refresh"
            disabled={isRefreshing}
            onPress={() => onClickRefresh()}
          />
          <Appbar.Action
            icon={
              showComments ? 'comment-eye' : 'comment-eye-outline'
            }
            onPress={() => onClickShowComments()}
          />

          <Appbar.Action
            icon={'comment-plus'}
            onPress={() => onClickAddComment()}
          />
          {showEditor && (
            <Appbar.Action
              disabled={isRefreshing}
              icon="content-cut"
              onPress={() => onClickCut()}
            />
          )}
          {showEditor && (
            <Appbar.Action
              disabled={isRefreshing}
              icon="content-copy"
              onPress={() => onClickCopy()}
            />
          )}
        </>
      ) : (
        <Menu
          visible={menuOpen}
          onDismiss={onHideMenu}
          anchor={
            <Appbar.Action icon={'dots-grid'} onPress={openMenu} />
          }>
          <Menu.Item
            leadingIcon="refresh"
            title="Refresh"
            disabled={isRefreshing}
            onPress={() => onClickRefresh()}
          />
          <Menu.Item
            leadingIcon={showConfig ? 'cog' : 'cog-outline'}
            title="Config"
            onPress={() => {
              dispatch(setShowConfig(showConfig ? undefined : true));
              onHideMenu();
            }}
          />
          <Menu.Item
            title="Toggle comments"
            leadingIcon={
              showComments ? 'comment-eye' : 'comment-eye-outline'
            }
            onPress={() => onClickShowComments()}
          />
          <Menu.Item
            title="Add comment"
            leadingIcon={'comment-plus'}
            onPress={() => onClickAddComment()}
          />
          {showEditor && (
            <Menu.Item
              title="Cut Content"
              disabled={isRefreshing}
              leadingIcon="content-cut"
              onPress={() => onClickCut()}
            />
          )}
          {showEditor && (
            <Menu.Item
              title="Copy Content"
              disabled={isRefreshing}
              leadingIcon="content-copy"
              onPress={() => onClickCopy()}
            />
          )}
        </Menu>
      )}
    </>
  );
}
